import { css } from '@emotion/react';
import {
  faCheck,
  faMinus,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useEffect, useState,
} from 'react';
import {
  ButtonComponent,
  ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { RegularDropdownComponent } from '~/_shared/baseComponents/dropdown/regularDropdown.component';
import {
  InputSize, TextInputComponent,
} from '~/_shared/baseComponents/inputs';
import { AddMorePlaceholderComponent } from '~/_shared/components/addMorePlaceholder/addMorePlaceholder.component';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { createUuid } from '~/_shared/utils/createUuid';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ModalProps } from '~/modal/modalType.enum';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type {
  BoundaryGroupDemographic, DemographicIdCode,
} from '~/store/boundaryGroups/boundaryGroups.repository';
import { getChartMetricTypeTranslation } from '~/store/mapSettings/data/charts/mapSettingsCharts.helpers';
import { ChartMetricType } from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import type { GroupId } from '~/store/spreadsheetData/spreadsheetData.state';

export type ChartEditorData = Readonly<{
  name: string;
  boundaryGroupId?: number;
  metrics: ReadonlyArray<{
    dataColumn: ColumnId | null;
    demographic: DemographicIdCode | null;
    type: ChartMetricType;
    columnValue: GroupId | null;
  }>;
}>;

const modalContentStyle = css({
  padding: 20,
  width: '100%',
  boxSizing: 'border-box',
});

const headingStyle = css({
  margin: '0 0 10px',
  textTransform: 'uppercase',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.2,
});

const metricsSectionStyle = css({
  marginTop: '24px',
});

const metricsTitleStyle = (textColor: string) => css({
  fontSize: 18,
  color: textColor,
  marginBottom: 16,
  textTransform: 'uppercase',
});

const metricRowStyle = css({
  display: 'flex',
  gap: 12,
  marginBottom: 24,
});

const labelStyle = (textColor: string) => css({
  fontSize: 14,
  color: textColor,
  marginBottom: 4,
});

const fieldContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const deleteButtonStyle = css({
  cursor: 'pointer',
  marginTop: 27,
});

const secondDropdownStyle = css({
  marginTop: 4,
});

const getNewMetricRow = ({
  dataColumn,
  demographic,
  type,
  columnValue,
}: {
  dataColumn?: ChartEditorDataColumn;
  demographic?: DemographicIdCode;
  type?: ChartMetricType;
  columnValue?: GroupId;
} = {}): ChartEditorMetricRow => ({
  id: createUuid(),
  dataColumn: dataColumn || null,
  demographic: demographic || null,
  type: type || ChartMetricType.SpreadsheetColumn,
  columnValue: columnValue ?? null,
});

const isValidMetric = (metric: ChartEditorMetricRow) => {
  switch (metric.type) {
    case ChartMetricType.SpreadsheetColumn:
      return metric.dataColumn !== null;
    case ChartMetricType.Demographic:
      return metric.demographic !== null;
    case ChartMetricType.LocationCount:
      return metric.dataColumn === ALL_LOCATIONS_COUNT || (
        metric.columnValue !== null && metric.dataColumn !== null
      );
    default:
      return false;
  }
};

const ALL_LOCATIONS_COUNT = 'ALL_LOCATIONS_COUNT';
type ChartEditorDataColumn = typeof ALL_LOCATIONS_COUNT | ColumnId;

type ChartEditorMetricRow = Readonly<{
  id: string;
  dataColumn: ChartEditorDataColumn | null;
  demographic: DemographicIdCode | null;
  type: ChartMetricType;
  columnValue: GroupId | null;
}>;

export const ChartEditorModalComponent = ({
  boundaryGroupOptions,
  dataColumnOptions,
  locationCountColumnOptions,
  defaultBoundaryGroupId,
  defaultMetrics,
  defaultName,
  demographicsMap,
  isLoading,
  onSave,
  ...modalProps
}: ModalProps<{
  boundaryGroupOptions?: ReadonlyArray<{ name: string; id: BoundaryGroupId }>;
  dataColumnOptions: ReadonlyArray<{ name: string; value: string }>;
  locationCountColumnOptions: ReadonlyArray<{
    name: string;
    value: string;
    groupValues: ReadonlyArray<{ name: string; value: GroupId }>;
  }>;
  defaultBoundaryGroupId?: BoundaryGroupId;
  defaultMetrics?: ReadonlyArray<Omit<ChartEditorMetricRow, 'id' | 'type'> & Readonly<{ type: ChartMetricType }>>;
  defaultName?: string;
  demographicsMap: ReadonlyMap<BoundaryGroupId, ReadonlyArray<BoundaryGroupDemographic>>;
  isLoading?: boolean;
  onSave: (params: ChartEditorData) => void;
}>) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const [chartName, setChartName] = useState('');
  const [metrics, setMetrics] = useState<ChartEditorMetricRow[]>([]);
  const [selectedBoundaryGroupId, setSelectedBoundaryGroupId] = useState<BoundaryGroupId | null>(null);

  const allLocationsColumnOption = {
    name: t('All Locations'),
    value: ALL_LOCATIONS_COUNT,
  };

  const isFormValid =
    chartName.trim().length > 0 &&
    metrics.length > 0 &&
    metrics.every(isValidMetric)
    && (!boundaryGroupOptions || selectedBoundaryGroupId !== null);

  const allChartMetricTypeOptions = Object.values(ChartMetricType).map(type => ({
    name: getChartMetricTypeTranslation(type, t),
    value: type,
  }));

  const editableChartMetricTypeOptions = allChartMetricTypeOptions.filter(option =>
    option.value !== ChartMetricType.Demographic
  );

  const demographicOptions = selectedBoundaryGroupId
    ? (demographicsMap.get(selectedBoundaryGroupId) || []).map(demographic => ({
      name: demographic.name,
      value: demographic.id,
    })) || []
    : [];

  const addMetricRow = useCallback(() => {
    const newMetric = getNewMetricRow();
    setMetrics([...metrics, newMetric]);
  }, [metrics]);

  const deleteMetricRow = (id: string) => {
    setMetrics(metrics.filter(metric => metric.id !== id));
  };

  const handleSave = useCallback(() => {
    onSave({
      boundaryGroupId: boundaryGroupOptions && selectedBoundaryGroupId ? selectedBoundaryGroupId : undefined,
      name: chartName,
      metrics: metrics.map(metric => ({
        type: metric.type,
        dataColumn: metric.dataColumn === ALL_LOCATIONS_COUNT ? null : metric.dataColumn,
        demographic: metric.demographic,
        columnValue: metric.columnValue,
      })),
    });
  }, [chartName, metrics, onSave, boundaryGroupOptions, selectedBoundaryGroupId]);

  const updateMetricField = <K extends keyof ChartEditorMetricRow>(
    id: string,
    field: K,
    value: ChartEditorMetricRow[K]
  ) => {
    setMetrics(metrics.map(metric =>
      metric.id === id ? { ...metric, [field]: value } : metric
    ));
  };

  // Changing the metric type invalidates any previously selected data source, so reset all dependent fields.
  const updateMetricType = (id: string, type: ChartMetricType) => {
    setMetrics(metrics.map(metric =>
      metric.id === id ? { ...metric, type, dataColumn: null, demographic: null, columnValue: null } : metric
    ));
  };

  // Changing the selected column invalidates the previously selected column value.
  const updateMetricDataColumn = (id: string, dataColumn: ChartEditorDataColumn) => {
    setMetrics(metrics.map(metric =>
      metric.id === id ? { ...metric, dataColumn, columnValue: null } : metric
    ));
  };

  const handleBoundaryGroupChange = useCallback((boundaryGroupId: BoundaryGroupId) => {
    setSelectedBoundaryGroupId(boundaryGroupId);
    setChartName(boundaryGroupOptions?.find(bg => bg.id === boundaryGroupId)?.name ?? '');
  }, [boundaryGroupOptions]);

  useEffect(() => {
    if (boundaryGroupOptions?.length === 1 && boundaryGroupOptions[0]) {
      handleBoundaryGroupChange(boundaryGroupOptions[0].id);
    }
  }, [boundaryGroupOptions, handleBoundaryGroupChange]);

  useEffect(() => {
    if (defaultBoundaryGroupId) {
      setSelectedBoundaryGroupId(defaultBoundaryGroupId);
    }
  }, [defaultBoundaryGroupId]);

  useEffect(() => {
    if (defaultName) {
      setChartName(defaultName);
    }
  }, [defaultName]);

  useEffect(() => {
    if (defaultMetrics) {
      setMetrics(defaultMetrics.map(metric => getNewMetricRow({
        dataColumn: (
          metric.type === ChartMetricType.LocationCount && metric.dataColumn === null
            ? ALL_LOCATIONS_COUNT
            : metric.dataColumn
        ) || undefined,
        demographic: metric.demographic || undefined,
        type: metric.type,
        columnValue: metric.columnValue || undefined,
      })));
    }
  }, [defaultMetrics]);

  return (
    <ModalComponent
      {...modalProps}
      additionalContent={isLoading ? <OverlayLoaderComponent /> : null}
      caption={t('Calculating Statistics')}
      contentStyle={modalContentStyle}
      maxWidth="643px"
      confirmButton={(
        <ButtonComponent
          prefixIcon={faCheck}
          buttonStyle={ButtonStyle.Primary}
          text={t('Done')}
          onClick={isFormValid ? handleSave : undefined}
          isDisabled={isLoading || !isFormValid}
        />
      )}
    >
      {boundaryGroupOptions && boundaryGroupOptions.length > 1 && (
        <div css={{ marginBottom: '10px' }}>
          <p css={headingStyle}>{t('Select Territory Group')}</p>
          <RegularDropdownComponent
            value={selectedBoundaryGroupId}
            options={boundaryGroupOptions.map(option => ({
              name: option.name,
              value: option.id,
            }))}
            onChange={handleBoundaryGroupChange}
            placeholder={t('Select Territory Group')}
          />
        </div>
      )}

      <div>
        <p css={headingStyle}>{t('chartEditor.title.label')}</p>
        <TextInputComponent
          onChange={setChartName}
          placeholder={t('Chart')}
          value={chartName}
        />
      </div>

      <div css={metricsSectionStyle}>
        <h2 css={metricsTitleStyle(theme.textColors.info)}>{t('chartEditor.metrics.caption')}</h2>

        {metrics.map((metric) => {
          const isEditable = metric.type !== ChartMetricType.Demographic;
          const commonDropdownProps = {
            inPortal: true,
            isDisabled: !isEditable,
            inputSize: InputSize.Small,
          };

          return (
            <div key={metric.id} css={metricRowStyle}>
              <div css={deleteButtonStyle}>
                <HoverableIconComponent
                  icon={faMinus}
                  hoverColor={theme.iconColors.borderDangerInvertedHover}
                  baseColor={theme.iconColors.danger}
                  onClick={isEditable ? () => deleteMetricRow(metric.id) : undefined}
                  isDisabled={!isEditable}
                  title={t('Delete')}
                />
              </div>

              <div css={fieldContainerStyle}>
                <label css={labelStyle(theme.textColors.primary)}>
                  {t('Select Metric Type')}
                </label>
                <RegularDropdownComponent
                  {...commonDropdownProps}
                  value={metric.type}
                  options={isEditable ? editableChartMetricTypeOptions : allChartMetricTypeOptions}
                  onChange={isEditable ? (value) => updateMetricType(metric.id, value) : noop}
                  placeholder={t('Select Data Source')}
                />
              </div>

              <div css={fieldContainerStyle}>
                <label css={labelStyle(theme.textColors.primary)}>
                  {metric.type ? getChartMetricTypeTranslation(metric.type, t) : t('Select')}
                </label>
                {metric.type === ChartMetricType.SpreadsheetColumn && (
                  <RegularDropdownComponent
                    {...commonDropdownProps}
                    value={metric.dataColumn}
                    options={dataColumnOptions.filter(option =>
                      !metrics
                        .filter(m => m.id !== metric.id)
                        .some(metric => metric.type === ChartMetricType.SpreadsheetColumn && metric.dataColumn === option.value))
                    }
                    onChange={isEditable ? (value) => updateMetricDataColumn(metric.id, value) : noop}
                    placeholder={t('Select Data Column')}
                  />
                )}
                {metric.type === ChartMetricType.Demographic && (
                  <RegularDropdownComponent
                    {...commonDropdownProps}
                    value={metric.demographic}
                    options={demographicOptions}
                    onChange={isEditable ? (value) => updateMetricField(metric.id, 'demographic', value) : noop}
                    placeholder={t('Select Demographic')}
                  />
                )}
                {metric.type === ChartMetricType.LocationCount && (
                  <RegularDropdownComponent
                    {...commonDropdownProps}
                    value={metric.dataColumn}
                    options={[
                      allLocationsColumnOption,
                      ...locationCountColumnOptions.map(option => ({ name: option.name, value: option.value })),
                    ].filter(option =>
                      !metrics
                        .filter(m => m.id !== metric.id)
                        .some(metric => metric.type === ChartMetricType.LocationCount
                          && metric.dataColumn === ALL_LOCATIONS_COUNT && option.value === ALL_LOCATIONS_COUNT
                        ))
                    }
                    onChange={isEditable ? (value) => updateMetricDataColumn(metric.id, value) : noop}
                    placeholder={t('Select Data Column')}
                  />
                )}
                {metric.type === ChartMetricType.LocationCount && metric.dataColumn !== ALL_LOCATIONS_COUNT && metric.dataColumn !== null && (
                  <RegularDropdownComponent
                    {...commonDropdownProps}
                    css={secondDropdownStyle}
                    value={metric.columnValue}
                    options={(locationCountColumnOptions.find(option => option.value === metric.dataColumn)?.groupValues ?? [])
                      .filter(groupOption =>
                        !metrics
                          .filter(m => m.id !== metric.id)
                          .some(m => m.type === ChartMetricType.LocationCount
                            && m.dataColumn === metric.dataColumn && m.columnValue === groupOption.value
                          ))
                    }
                    onChange={isEditable ? (value) => updateMetricField(metric.id, 'columnValue', value) : noop}
                    placeholder={t('Select Group')}
                  />
                )}
              </div>
            </div>
          );
        })}

        <AddMorePlaceholderComponent
          title={t('chartEditor.metrics.add')}
          onClick={addMetricRow}
        />
      </div>
    </ModalComponent>
  );
};
